import { useCallback, useEffect, useReducer, useRef } from 'react';
import api from '../util/api';
import { getProductsByIdFromResponse } from '../util/uploadCartSync';
import {
  EMPTY_FULL_CART_STATE, FULL_CART_TTL_MS, fullCartPersistenceReducer,
  fullCartStorageKey, getMissingFullCartProductIds, readFullCartSnapshot, restoreFullCartItems, writeFullCartSnapshot,
} from '../util/fullCartPersistence';

const getStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; }
  catch { return null; }
};

export default function usePersistentFullCart(userId) {
  const owner = userId ? String(userId) : null;
  const [state, dispatch] = useReducer(fullCartPersistenceReducer, EMPTY_FULL_CART_STATE);
  const activationRef = useRef(0);
  const cart = state.userId === owner ? state.cart : EMPTY_FULL_CART_STATE.cart;
  const setCart = useCallback(value => {
    dispatch({ type: 'update', value, userId: owner, now: Date.now() });
  }, [owner]);
  const deactivateFullCart = useCallback(() => {
    activationRef.current += 1;
    dispatch({ type: 'deactivate' });
  }, []);
  const activateFullCart = useCallback(async (integrationId, products, backendCart) => {
    const activation = ++activationRef.current;
    const key = fullCartStorageKey(owner, integrationId);
    if (!key) return;
    const now = Date.now();
    const snapshot = readFullCartSnapshot(getStorage(), key, now);
    const missingIds = getMissingFullCartProductIds(snapshot, products);
    const supplemental = [];
    for (let offset = 0; offset < missingIds.length; offset += 100) {
      const response = await api.get('/api/products/by-ids', { params: { ids: missingIds.slice(offset, offset + 100).join(',') } });
      if (activation !== activationRef.current) return;
      supplemental.push(...Object.values(getProductsByIdFromResponse(response))
        .map(product => ({ id: product.id, productId: product.id, product })));
    }
    if (activation !== activationRef.current) return;
    dispatch({ type: 'activate', state: {
      key, userId: owner, cart: restoreFullCartItems(snapshot, [...products, ...supplemental], backendCart), expiresAt: now + FULL_CART_TTL_MS,
    } });
  }, [owner]);
  useEffect(() => {
    if (state.userId === owner) writeFullCartSnapshot(getStorage(), state.key, state.cart, state.expiresAt);
  }, [state, owner]);
  return { cart, setCart, activateFullCart, deactivateFullCart };
}
